<template>
  <div>
    <v-card
      :outlined="$vuetify.breakpoint.lgAndUp"
      :flat="$vuetify.breakpoint.mdAndDown"
      class="results-options-card"
    >
      <!-- SMALL DEVICES CLOSE -->
      <v-btn
        v-if="$vuetify.breakpoint.mdAndDown"
        rounded
        depressed
        outlined
        color="white"
        small
        class="mt-3"
        @click="$emit('click-close')"
      >
        <v-icon
          color="primary"
          class="mr-1"
        >
          mdi-close-circle
        </v-icon>
        <span class="primary--text text-decoration-underline">Close</span>
      </v-btn>

      <!-- TITLE -->
      <v-card-title
        class="py-3"
      >
        Adjust Cover Options
      </v-card-title>

      <v-card-text>
        <!-- EDIT/SUMMARY BTNS -->
        <v-row dense>
          <v-col>
            <v-btn
              block
              outlined
              rounded
              color="primary"
              @click.prevent="onClickEditQuote"
            >
              <v-icon
                small
                class="mr-1"
              >
                mdi-pencil
              </v-icon>
              <span class="caption font-weight-medium">Edit Quote</span>
            </v-btn>
          </v-col>
          <v-col>
            <v-btn
              block
              outlined
              rounded
              color="primary"
              @click="showQuoteDetails"
            >
              <span class="caption font-weight-medium">Quote Summary</span>
            </v-btn>
          </v-col>
        </v-row>

        <!-- FILTERS -->
        <v-expansion-panels
          v-model="panelsShown"
          multiple
          accordion
          flat
        >
          <!-- ADDITIONAL COVER OPTIONS -->
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span>
                <strong>Additional Cover Options</strong>
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content v-scroll-to-viewport>
              <p class="body-2 mb-0">
                Additional cover options to enhance your policy.
              </p>
              <a
                href="#"
                class="caption text-decoration-underline d-block mb-2"
                @click.prevent="showCoverOptionsDetails"
              >
                Learn more about cover options
              </a>
              <v-checkbox
                v-for="(type, key) in coverTypes"
                :key="key"
                v-model="type.value"
                :label="`${type.title} Cover`"
                :value="type.value"
                hide-details
                dense
                class="mt-2 text-capitalize"
                @change="updateExtra(type)"
              />
            </v-expansion-panel-content>

            <div
              v-show="!panelsShown.includes(0)"
              class="v-expansion-panel-content__wrap"
            >
              <p
                v-if="optionalExtras && optionalExtras.length > 0"
                class="caption mb-0"
              >
                <span
                  v-for="(extratitle, key) in optionalExtras"
                  :key="`${key}`"
                >
                  {{ getOptionalExtrasTitle(extratitle) }}<span
                    v-if="key !== optionalExtras.length - 1"
                  >, </span>

                </span>
              </p>
              <p
                v-else
                class="caption mb-0"
              >
                Any
              </p>
            </div>
          </v-expansion-panel>

          <!-- COVER DETAILS OPTIONS (SLIDERS)-->
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span>
                Filter results by
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content v-scroll-to-viewport>
              <a
                href="#"
                class="caption d-block mt-2 text-decoration-underline"
                @click.prevent="showAnExcessDetails"
              >
                What is an excess?
              </a>
              <div
                v-for="panel in travelDropdowns"
                :key="panel.type"
              >
                <strong class="d-block my-3">
                  {{ panel.header }}
                </strong>
                <v-select
                  :items="panel.options"
                  :value="panel.selectedValue"
                  :label="panel.header"
                  single-line
                  attach
                  @change="value => updateFilters({type: panel.type, value})"
                />
              </div>
            </v-expansion-panel-content>
            <v-divider />

            <div
              v-show="!panelsShown.includes(1)"
              class="v-expansion-panel-content__wrap"
            >
              <p
                v-if="modifiedTravelDropdowns && modifiedTravelDropdowns.length"
                class="caption mb-0"
              >
                <span
                  v-for="(excessDropdownOption, index) in modifiedTravelDropdowns"
                  :key="`modified_travel_dropdown_${excessDropdownOption.type}`"
                >{{ excessDropdownOption.selectedValueText }}<span v-if="index !== modifiedTravelDropdowns.length - 1">,<br></span>
                </span>
              </p>
              <p
                v-else
                class="caption mb-0"
              >
                Any
              </p>
            </div>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-btn
          block
          outlined
          rounded
          color="primary"
          class="mt-2"
          @click="$emit('clear-filters')"
        >
          <span class="caption font-weight-medium">Clear All Filters</span>
        </v-btn>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { inject } from '@vue/composition-api';

export default {
  name: 'results-options',
  inject: ['constants'],
  props: {
    formData: {
      type: Object,
      default: () => {},
    },
    options: {
      type: Object,
      required: true,
    },
    selectedFilters: {
      type: Object,
      required: true,
    },
    detailsDescription: {
      type: Object,
      default: () => {},
    },
    isUpdateQuoteShow: {
      type: Boolean,
      default: false,
    },
    travelDropdowns: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    panelsShown: [0, 1, 2, 3, 4],
    initialRequoteVal: {},
  }),
  mounted () {
    this.initialRequoteVal = {
      typeOfCover: this.formData.trip?.typeOfPolicy,
    };
  },
  computed: {
    optionalExtras: {
      get () {
        return this.selectedFilters.optionalExtras;
      },
      set (value) {
        return this.$emit('change-filter', { filter: 'optionalExtras', value });
      },
    },
    coverTypes () {
      return [
        {
          coverType: 'gadget',
          value: this.optionalExtras.includes('gadget'),
          title: 'Gadget',
        },
        {
          coverType: 'winterSports',
          value: this.optionalExtras.includes('winterSports'),
          title: 'Winter Sports',
        },
        {
          coverType: 'cruise',
          value: this.optionalExtras.includes('cruise'),
          title: 'Cruise',
        }];
    },
    modifiedTravelDropdowns () {
      return this.travelDropdowns.filter(dropdown => !['any', 'any_excess'].includes(dropdown.selectedValue.key))
        .map(dropdown => {
          const text = dropdown.options.find(option => option.value.key === dropdown.selectedValue.key).text;
          dropdown.selectedValueText = `${dropdown.header}: ${text}`;
          return dropdown;
        });
    },
  },
  methods: {
    updateExtra ({ coverType, value }) {
      const existedOptionalExtra = this.optionalExtras.includes(coverType);
      let updatedExtras = [];

      if (existedOptionalExtra) {
        updatedExtras = this.optionalExtras.filter(extra => extra !== coverType);
      } else {
        updatedExtras.push(...this.optionalExtras, coverType);
      }

      this.$emit('change-filter', {
        filter: 'optionalExtras',
        value: updatedExtras,
      });

      this.$emit('change-requote-filter', {
        filter: `${coverType}Cover`,
        val: value ?? false,
      });

      this.internalTrackerResultsPage(this.constants.OPTIONAL_EXTRAS_TRACKER_OPTIONS[coverType], updatedExtras.includes(coverType));

      this.$emit('re-quote');
    },
    updateFilters ({ type, value }) {
      this.$emit('update-results', {
        type,
        value,
      });
    },
    showQuoteDetails () {
      this.internalTrackerResultsPage('quoteSummary', true);
      this.$emit('click-details', ['details-quote-summary']);
    },
    showCoverOptionsDetails () {
      this.$emit('click-details', ['details-description', 'coverOptions']);
    },
    showAnExcessDetails () {
      this.$emit('click-details', ['details-description', 'anExcess']);
    },
    onClickEditQuote () {
      this.internalTrackerResultsPage('editQuote', true);
      return this.$emit('click-edit-quote');
    },
    handleClickCoverType (filter) {
      return this.$emit('change-filter', filter);
    },
    getOptionalExtrasTitle (key) {
      const optionalExtraTitle = this.detailsDescription.optionalExtras?.content.find((el) => el.slug === key);
      if (optionalExtraTitle) {
        return optionalExtraTitle.name;
      }
      return key;
    },
  },
  setup () {
    const internalTrackers = inject('internalTrackers');

    return { ...internalTrackers };
  },
};
</script>

<style scoped lang="scss" src='@/assets/scss/results/results-options.scss'></style>

<style>
  .v-expansion-panel-header {
    padding: 16px 0;
  }

  .v-expansion-panel-content__wrap {
    padding: 0 0 16px;
  }
</style>
